import createClient, { type Middleware } from 'openapi-fetch'

import { env } from '@/lib/env'

import type { components, paths } from './schema'
import { expiresWithin } from './token'

/**
 * The one HTTP client in the app, and how its requests are signed in (docs/ARCHITECTURE.md,
 * Tenancy and auth).
 *
 * Every request goes through `api`, from a TanStack Query hook in `features/<x>/queries.ts`. The
 * middleware below attaches the access token and, in an org's scope, the org (`X-Org-ID`),
 * refreshes a token that is about to run out, and answers a 401 with one refresh and one retry.
 * The session's rules (who is signed in, signing out, where a visitor is sent) are
 * `features/auth/session.ts`'s.
 */

const TOKEN_KEY = 'damis.access_token'
const ORG_KEY = 'damis.org_id'

type Listener = () => void

/**
 * One string persisted in localStorage that React can subscribe to (`useSyncExternalStore`).
 * Storage may be unavailable (private mode), in which case the value does not survive a reload.
 */
export function persistedValue(key: string) {
  const listeners = new Set<Listener>()
  const notify = () => {
    for (const listener of listeners) listener()
  }

  // Keep tabs in step: signing out in one tab signs out the others.
  if (typeof window !== 'undefined') {
    window.addEventListener('storage', (event) => {
      if (event.key === key || event.key === null) notify()
    })
  }

  return {
    get(): string | null {
      try {
        return localStorage.getItem(key)
      } catch {
        return null
      }
    },
    set(value: string | null) {
      try {
        if (value) localStorage.setItem(key, value)
        else localStorage.removeItem(key)
      } catch {
        /* storage unavailable (private mode) */
      }
      notify()
    },
    subscribe(listener: Listener): () => void {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
  }
}

/** The bearer access token. Fifteen minutes long; the refresh cookie behind it is not readable. */
export const tokenStore = persistedValue(TOKEN_KEY)

/**
 * Where the signed-in user is working: the platform (the operator's console, ADR 0002) or one
 * org. Only an org scope names an org to the API.
 */
export type Scope = { readonly kind: 'platform' } | { readonly kind: 'org'; readonly orgId: string }

export const PLATFORM_SCOPE: Scope = Object.freeze({ kind: 'platform' })

export function orgScope(orgId: string): Scope {
  return { kind: 'org', orgId }
}

/**
 * The stored form of the platform scope. An org id is a UUID, so it can never be this word, and
 * the scope can live under the key that has always held the org id.
 */
const PLATFORM = 'platform'

/** The scope and the org share one stored string: an org id, `platform`, or nothing. */
const scopeValue = persistedValue(ORG_KEY)

/** The last scope read, so a reader in React gets the same object until the stored value moves. */
let lastScope: { stored: string | null; scope: Scope | null } = { stored: null, scope: null }

/**
 * The active scope, persisted in this browser: what the middleware below reads to decide whether
 * a request names an org. Nothing is set before the first sign-in, and after signing out.
 */
export const scopeStore = {
  get(): Scope | null {
    const stored = scopeValue.get()
    if (stored !== lastScope.stored) {
      const scope = stored === null ? null : stored === PLATFORM ? PLATFORM_SCOPE : orgScope(stored)
      lastScope = { stored, scope }
    }
    return lastScope.scope
  },
  set(scope: Scope | null): void {
    scopeValue.set(scope === null ? null : scope.kind === 'platform' ? PLATFORM : scope.orgId)
  },
  subscribe: scopeValue.subscribe,
}

/**
 * The org of an org scope, sent as `X-Org-ID`; none in the platform scope. Setting it enters that
 * org's scope. The API refuses an org the user is not in.
 */
export const orgStore = {
  get(): string | null {
    const scope = scopeStore.get()
    return scope?.kind === 'org' ? scope.orgId : null
  },
  set(orgId: string | null): void {
    scopeStore.set(orgId === null ? null : orgScope(orgId))
  },
  subscribe: scopeValue.subscribe,
}

/** Reached without the client below: it has no token to send, and must not recurse on a 401. */
const REFRESH_PATH = '/api/v1/auth/refresh'
/**
 * How close to its expiry a token is refreshed rather than sent: the flight time of the request it
 * would ride on, and a clock a little out of step.
 */
const REFRESH_MARGIN_MS = 60_000

let inFlight: Promise<boolean> | null = null

/** The API's code for a refusal because the platform operator suspended the org (story 20). */
export const ORG_SUSPENDED = 'org_suspended'

const suspendedListeners = new Set<() => void>()
let lastRefreshSuspended = false

/** Whether the latest refresh the API answered was refused because the org is suspended. */
export function refreshRefusedAsSuspended(): boolean {
  return lastRefreshSuspended
}

/**
 * Be told when the session ends because the member's org is suspended: a refresh or a request
 * refused with `org_suspended`. The listener runs before the token goes, so whatever reacts to
 * the token going already knows why. Returns the unsubscribe.
 */
export function onSuspended(listener: () => void): () => void {
  suspendedListeners.add(listener)
  return () => suspendedListeners.delete(listener)
}

/** Whether `response` is the API refusing because the org is suspended. */
async function isSuspension(response: Response): Promise<boolean> {
  if (response.status !== 403) return false
  try {
    const body = (await response.clone().json()) as { error?: { code?: unknown } } | null
    return body?.error?.code === ORG_SUSPENDED
  } catch {
    return false
  }
}

/** The member's org is suspended: say so to the listeners, then end the session here. */
function endAsSuspended(): void {
  for (const listener of suspendedListeners) listener()
  tokenStore.set(null)
}

/**
 * Trade the session cookie for a new access token. At most one of these is ever in the air.
 *
 * The refresh token is an httpOnly cookie no code here can read, which is the point, so this is a
 * bare `fetch` with credentials. Single flight because a screen fires several queries at once:
 * without it, the first refresh rotates the token and the others present one just spent, which
 * the API reads as a stolen token and answers by ending every session the user has.
 */
export function refreshSession(): Promise<boolean> {
  inFlight ??= (async () => {
    try {
      const response = await fetch(
        new Request(`${env.VITE_API_URL}${REFRESH_PATH}`, {
          method: 'POST',
          credentials: 'include',
        }),
      )
      lastRefreshSuspended = await isSuspension(response)
      if (!response.ok) {
        if (lastRefreshSuspended) endAsSuspended()
        return false
      }
      const body: unknown = await response.json()
      if (
        typeof body !== 'object' ||
        body === null ||
        !('access_token' in body) ||
        typeof body.access_token !== 'string'
      ) {
        return false
      }
      tokenStore.set(body.access_token)
      return true
    } catch {
      // Offline. The token we hold is not known to be bad, so it is left alone.
      return false
    } finally {
      inFlight = null
    }
  })()
  return inFlight
}

/** The token to send, refreshed first if it is about to run out under the request. */
async function freshToken(): Promise<string | null> {
  const token = tokenStore.get()
  if (token === null || !expiresWithin(token, REFRESH_MARGIN_MS)) return token
  await refreshSession()
  return tokenStore.get()
}

/**
 * The request as it was sent, kept so a 401 can be answered by sending it again: a body is a
 * stream, spent by the fetch that carried it, so the copy is taken on the way out.
 */
const replayable = new WeakMap<Request, Request>()

const session: Middleware = {
  async onRequest({ request }) {
    const token = await freshToken()
    if (token) request.headers.set('Authorization', `Bearer ${token}`)
    // The platform scope works across orgs, so it names none. A request that names its org
    // itself (loading an org before switching to it) keeps the one it names.
    const scope = scopeStore.get()
    if (scope?.kind === 'org' && !request.headers.has('X-Org-ID')) {
      request.headers.set('X-Org-ID', scope.orgId)
    }
    if (request.body !== null) replayable.set(request, request.clone())
    return request
  },
  async onResponse({ request, response }) {
    if (!request.headers.has('Authorization')) return response
    // The org was suspended under the member: the session ends, saying why (story 20).
    if (await isSuspension(response)) {
      endAsSuspended()
      return response
    }
    if (response.status !== 401) return response
    // Rejected though it looked live: the session was revoked, the secret rotated, a clock is
    // out. One refresh decides which, and the request goes again on the new token. Only once: a
    // 401 that survives the refresh is an answer about the user, so the token goes and the app
    // signs out, whether it was the refresh or the retried request that said so.
    if (!(await refreshSession())) {
      tokenStore.set(null)
      return response
    }
    const retry = replayable.get(request) ?? request
    retry.headers.set('Authorization', `Bearer ${tokenStore.get() ?? ''}`)
    const retried = await fetch(retry)
    if (retried.status === 401) tokenStore.set(null)
    return retried
  },
}

/** Fully typed client generated from the FastAPI OpenAPI schema (`pnpm api:types`). */
export const api = createClient<paths>({
  baseUrl: env.VITE_API_URL,
  // The refresh cookie rides along on /auth requests.
  credentials: 'include',
  // Resolve `fetch` per call rather than at import time, so a later polyfill or test stub applies.
  fetch: (request) => globalThis.fetch(request),
})
api.use(session)

export type Schemas = components['schemas']

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly details: unknown

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.details = details
  }
}

type ErrorEnvelope = {
  error?: { code?: string; message?: string; details?: unknown }
}

/** What one part of a multipart body may hold. A nullish part is left off the form. */
type Part = string | number | boolean | Blob | null | undefined

/** The typed body's view of `Fields`: every file part is the string the contract types it as. */
type Wire<Fields> = { [K in keyof Fields]: Fields[K] extends Blob ? string : Fields[K] }

/**
 * The `body` and `bodySerializer` of a multipart request, from the fields it carries (ported
 * from Forecourt, which had it from DealerDrive).
 *
 * The generated contract types every file part as a string, because OpenAPI 3.1 has no `binary`
 * format for the generator to map, so a typed `body` cannot hold the `File` the wire carries.
 * The serialiser builds the form from `fields` and never reads the typed body; the cast that
 * bridges the two lives here and nowhere else. A `null` or `undefined` field is left off the
 * form; a `File` keeps its name.
 */
export function multipart<Fields extends Record<string, Part>>(
  fields: Fields,
): { body: Wire<Fields>; bodySerializer: () => FormData } {
  return {
    // Reason for the double assertion: `Fields` and `Wire<Fields>` differ only in the file parts,
    // which is exactly the lie the contract tells and this function exists to contain.
    body: fields as unknown as Wire<Fields>,
    bodySerializer: () => {
      const form = new FormData()
      for (const [name, value] of Object.entries(fields)) {
        if (value === null || value === undefined) continue
        if (typeof value === 'object') form.append(name, value)
        else form.append(name, String(value))
      }
      return form
    },
  }
}

/** Convert an openapi-fetch result into data-or-throw so TanStack Query can handle errors. */
export function unwrap<T>(result: { data?: T; error?: unknown; response: Response }): T {
  if (result.error !== undefined || result.data === undefined) {
    const body = (result.error ?? {}) as ErrorEnvelope
    throw new ApiError(
      result.response.status,
      body.error?.code ?? 'unknown_error',
      body.error?.message ?? result.response.statusText,
      body.error?.details,
    )
  }
  return result.data
}
